// Confronto tra regime forfettario e regime ordinario a parita' di attivita'.
//
// Serve entrambe le pagine: il calcolatore ordinario mostra "quanto avresti nel
// forfettario" (solo se i requisiti ci sono), quello forfettario mostra "quanto
// avresti nell'ordinario" — informazione decisiva quando il fatturato supera la
// soglia degli 85.000 EUR e il forfettario non e' piu' una scelta possibile.
//
// Modulo puro: nessun DOM, cosi' e' testabile e condivisibile.
import { calcolaPivaOrdinaria } from './pivaOrdinariaCalc.js';
import {
    calcolaForfettario,
    redditoDipendenteAmmesso,
    SOGLIA_FORFETTARIO,
    SOGLIA_USCITA_IMMEDIATA,
    TETTO_REDDITO_DIPENDENTE
} from './forfettarioCalc.js';

export { SOGLIA_FORFETTARIO, SOGLIA_USCITA_IMMEDIATA, TETTO_REDDITO_DIPENDENTE };

// Coefficienti di redditivita' previsti dalla L. 190/2014, allegato 4.
export const COEFFICIENTI = [
    { valore: 40, label: 'Commercio, alimentari e bevande, ambulanti (40%)' },
    { valore: 54, label: 'Ambulanti di altri prodotti (54%)' },
    { valore: 62, label: 'Intermediari del commercio (62%)' },
    { valore: 67, label: 'Costruzioni, artigianato e attività immobiliari (67%)' },
    { valore: 78, label: 'Professionisti e attività scientifiche (78%)' },
    { valore: 86, label: 'Altre attività economiche (86%)' }
];

// Coefficiente plausibile a partire dalla forma di attivita' scelta
// nell'ordinario, che non chiede il codice ATECO.
export function coefficienteDefault(formaAttivita) {
    switch (formaAttivita) {
        case 'ditta_commerciale': return 40;
        case 'ditta_artigiana': return 67;
        case 'libero_senza_cassa':
        case 'libero_con_cassa': return 78;
        default: return 78;
    }
}

// Requisiti di accesso verificabili dai dati che i calcolatori gia' chiedono.
// Quelli che non possiamo conoscere (partecipazioni societarie, prevalenza
// verso l'ex datore di lavoro, spese per dipendenti oltre 20.000 EUR) escono
// come avvertenze, non come esclusioni.
export function valutaAmmissibilitaForfettario(input = {}) {
    const ricavi = Math.max(0, parseFloat(input.ricavi) || 0);
    const ralDipendente = parseFloat(input.ralDipendente) || 0;
    const haRedditoDipendente = Boolean(input.dipendenteFullTime) && ralDipendente > 0;

    if (ricavi > SOGLIA_USCITA_IMMEDIATA) {
        return {
            ammissibile: false,
            causa: 'ricavi_oltre_100k',
            motivo: `Con ricavi oltre ${SOGLIA_USCITA_IMMEDIATA.toLocaleString('it-IT')} € il forfettario decade subito, già dall'anno in corso.`
        };
    }
    if (ricavi > SOGLIA_FORFETTARIO) {
        return {
            ammissibile: false,
            causa: 'ricavi_oltre_soglia',
            motivo: `Il forfettario si può usare fino a ${SOGLIA_FORFETTARIO.toLocaleString('it-IT')} € di ricavi o compensi: sopra questa soglia si passa all'ordinario dall'anno successivo.`
        };
    }
    if (haRedditoDipendente && !redditoDipendenteAmmesso(ralDipendente)) {
        return {
            ammissibile: false,
            causa: 'reddito_dipendente',
            motivo: `Con più di ${TETTO_REDDITO_DIPENDENTE.toLocaleString('it-IT')} € di reddito da lavoro dipendente o pensione non si può accedere al forfettario.`
        };
    }
    return { ammissibile: true, causa: null, motivo: '' };
}

// Costi impliciti nel coefficiente di redditivita': e' la stima di costi che il
// forfettario stesso fa: il (100 - coefficiente)% del fatturato. E' il punto di
// partenza piu' onesto quando l'utente arriva dal forfettario, dove i costi
// reali non vengono mai chiesti.
export function costiImpliciti(fatturato, coefficienteRedditivita) {
    const f = Math.max(0, parseFloat(fatturato) || 0);
    const c = Math.min(Math.max(parseFloat(coefficienteRedditivita) || 0, 0), 100);
    return Math.round(f * (1 - c / 100));
}

// input: unione dei parametri dei due motori. `data` e' data/data.json.
export function confrontaRegimi(input, data) {
    const ricavi = Math.max(0, parseFloat(input.ricavi) || 0);

    // Sopra la soglia un forfettario "a parita' di fatturato" non esiste: i suoi
    // numeri sarebbero pura invenzione. L'unica domanda sensata diventa "e se
    // limitassi il fatturato a 85.000 € per restare nel regime agevolato?": il
    // forfettario si calcola quindi sul massimo consentito.
    const fatturatoForfettario = Math.min(ricavi, SOGLIA_FORFETTARIO);
    const forfettarioLimitato = fatturatoForfettario < ricavi;

    const ordinario = calcolaPivaOrdinaria({
        ricavi: input.ricavi,
        costiDeducibili: input.costiDeducibili,
        gestione: input.gestione,
        mesiAttivita: input.mesiAttivita,
        nuovaAttivitaArtCom: input.nuovaAttivitaArtCom,
        riduzione50: input.riduzione50,
        ante1996: input.ante1996,
        numeroMandanti: input.numeroMandanti,
        fasciaEtaEnpam: input.fasciaEtaEnpam,
        contabilitaOrdinaria: input.contabilitaOrdinaria,
        dipendenteFullTime: input.dipendenteFullTime,
        riduzioneDipendente: input.riduzioneDipendente,
        ralDipendente: input.ralDipendente,
        regione: input.regione,
        comune: input.comune,
        year: input.year
    }, data);

    const forfettario = calcolaForfettario({
        fatturato: fatturatoForfettario,
        coefficienteRedditivita: input.coefficienteRedditivita,
        aliquotaImposta: input.aliquotaImposta,
        gestione: input.gestione,
        mesiAttivita: input.mesiAttivita,
        nuovaAttivitaArtCom: input.nuovaAttivitaArtCom,
        riduzione50: input.riduzione50,
        riduzione35: input.riduzione35,
        numeroMandanti: input.numeroMandanti,
        fasciaEtaEnpam: input.fasciaEtaEnpam,
        dipendenteFullTime: input.dipendenteFullTime,
        riduzioneDipendente: input.riduzioneDipendente,
        ralDipendente: input.ralDipendente
    });

    const ammissibilita = valutaAmmissibilitaForfettario(input);

    // Ogni regime mostra il SUO netto, come lo calcolano le rispettive pagine:
    // l'ordinario deduce i costi reali, il forfettario li ignora e non li
    // sottrae. E' la stessa cifra che l'utente ritrova aprendo i due
    // calcolatori, e la nota sotto la card ricorda che nel forfettario i costi
    // restano comunque a carico.
    const costiReali = Math.max(0, parseFloat(input.costiDeducibili) || 0);
    const nettoOrdinario = ordinario.nettoAnnuo;
    const nettoForfettario = forfettario.nettoAnnuo;
    const differenza = nettoForfettario - nettoOrdinario;

    return {
        ordinario,
        forfettario,
        ammissibilita,
        costiReali,
        // Fatturato su cui e' calcolato il forfettario: coincide con i ricavi
        // sotto la soglia, altrimenti e' lo scenario "limitato a 85.000 €".
        fatturatoForfettario,
        forfettarioLimitato,
        nettoOrdinario,
        nettoForfettario,
        // `migliore` descrive solo la convenienza economica: se il forfettario
        // non e' accessibile la scelta non esiste, e va letto insieme ad
        // ammissibilita.ammissibile.
        migliore: differenza === 0 ? 'pari' : (differenza > 0 ? 'forfettario' : 'ordinario'),
        differenza,
        differenzaAssoluta: Math.abs(differenza),
        differenzaMensile: Math.round(Math.abs(differenza) / 12)
    };
}
